import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { aiConfigService } from "@/api/aiConfig";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";

const AiConfiguration = () => {
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState({
        endpoint: "",
        apiKey: "",
        deploymentName: "",
        temperature: 0,
        maxTokens: 0,
    });

    const { data: config, isLoading, error } = useQuery({
        queryKey: ["ai-config"],
        queryFn: aiConfigService.getConfig,
        retry: false,
    });

    const updateConfigMutation = useMutation({
        mutationFn: (data) => aiConfigService.updateConfig(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["ai-config"]);
            alert("Configuration IA mise à jour avec succès !");
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert("Erreur lors de la mise à jour de la configuration");
        },
    });

    useEffect(() => {
        if (config) {
            setFormData({
                endpoint: config.endpoint || "",
                apiKey: config.apiKey || "",
                deploymentName: config.deploymentName || "",
                temperature: config.temperature ?? 0,
                maxTokens: config.maxTokens ?? 0,
            });
        }
    }, [config]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        if (!formData.endpoint || !formData.apiKey || !formData.deploymentName) {
            alert("Veuillez remplir tous les champs obligatoires");
            return;
        }
        updateConfigMutation.mutate({
            ...formData,
            temperature: parseFloat(formData.temperature),
            maxTokens: parseInt(formData.maxTokens),
        });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    return (
        <TaleDashbaordMainCard className="p-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Configuration IA</h1>
                <p className="text-gray-600 mt-2">
                    Configurez les paramètres du modèle d'intelligence artificielle
                </p>
                <hr />
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm text-blue-600 font-medium">Déploiement</div>
                            <div className="text-xl font-bold text-gray-900 truncate">
                                {config?.deploymentName || "—"}
                            </div>
                        </div>
                        <i className="fas fa-rocket text-3xl text-blue-400"></i>
                    </div>
                    <div className="mt-3 text-sm text-blue-700">Nom du déploiement actif</div>
                </div>

                <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm text-green-600 font-medium">Température</div>
                            <div className="text-2xl font-bold text-gray-900">
                                {config?.temperature ?? "—"}
                            </div>
                        </div>
                        <i className="fas fa-thermometer-half text-3xl text-green-400"></i>
                    </div>
                    <div className="mt-3 text-sm text-green-700">Créativité du modèle (0 = précis)</div>
                </div>

                <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm text-purple-600 font-medium">Max Tokens</div>
                            <div className="text-2xl font-bold text-gray-900">
                                {config?.maxTokens ?? "—"}
                            </div>
                        </div>
                        <i className="fas fa-align-left text-3xl text-purple-400"></i>
                    </div>
                    <div className="mt-3 text-sm text-purple-700">Longueur maximale des réponses</div>
                </div>
            </div>

            {/* Form Section */}
            <div className="space-y-8">
                <div className="border-t pt-8">
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                            <i className="fas fa-sliders-h text-blue-500 text-xl"></i>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Paramètres du modèle</h2>
                            <p className="text-gray-600 text-sm">
                                Mettez à jour les paramètres de connexion et de comportement du modèle IA
                            </p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <TaleInput
                                label="Endpoint *"
                                name="endpoint"
                                value={formData.endpoint}
                                onChange={handleInputChange}
                                placeholder="https://your-resource.openai.azure.com/"
                            />
                            <TaleInput
                                label="Nom du déploiement *"
                                name="deploymentName"
                                value={formData.deploymentName}
                                onChange={handleInputChange}
                                placeholder="Ex: gpt-4o"
                            />
                        </div>

                        <TaleInput
                            label="Clé API *"
                            name="apiKey"
                            value={formData.apiKey}
                            onChange={handleInputChange}
                            placeholder="Votre clé API"
                            type="password"
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <TaleInput
                                label="Température"
                                name="temperature"
                                type="number"
                                step="0.1"
                                min="0"
                                max="2"
                                value={formData.temperature}
                                onChange={handleInputChange}
                                placeholder="Ex: 0.7"
                                helperText="Entre 0 (déterministe) et 2 (créatif)"
                            />
                            <TaleInput
                                label="Max Tokens"
                                name="maxTokens"
                                type="number"
                                min="1"
                                value={formData.maxTokens}
                                onChange={handleInputChange}
                                placeholder="Ex: 2048"
                                helperText="Nombre maximum de tokens par réponse"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end mt-8">
                        <TaleButton
                            onClick={handleSubmit}
                            loading={updateConfigMutation.isPending}
                        >
                            Enregistrer la configuration
                        </TaleButton>
                    </div>
                </div>

                {/* Info Section */}
                <div className="border-t pt-8">
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mr-4">
                            <i className="fas fa-question-circle text-gray-500 text-xl"></i>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Guide des paramètres</h2>
                            <p className="text-gray-600 text-sm">Comprendre chaque paramètre</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-6 bg-blue-50 rounded-xl border border-blue-200">
                            <div className="flex items-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                    <i className="fas fa-link text-blue-500"></i>
                                </div>
                                <div className="font-semibold text-blue-700">Endpoint & Clé API</div>
                            </div>
                            <p className="text-sm text-blue-600">
                                L'URL du service Azure OpenAI et la clé d'authentification fournie dans votre portail Azure.
                            </p>
                        </div>

                        <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                            <div className="flex items-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                                    <i className="fas fa-thermometer-half text-green-500"></i>
                                </div>
                                <div className="font-semibold text-green-700">Température</div>
                            </div>
                            <p className="text-sm text-green-600">
                                Contrôle l'aléatoire des réponses. Une valeur faible (0.1) donne des réponses précises, une valeur élevée (1.5) des réponses plus créatives.
                            </p>
                        </div>

                        <div className="p-6 bg-purple-50 rounded-xl border border-purple-200">
                            <div className="flex items-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                                    <i className="fas fa-align-left text-purple-500"></i>
                                </div>
                                <div className="font-semibold text-purple-700">Max Tokens</div>
                            </div>
                            <p className="text-sm text-purple-600">
                                Limite la longueur des réponses générées. Un token correspond environ à ¾ d'un mot en anglais.
                            </p>
                        </div>

                        <div className="p-6 bg-orange-50 rounded-xl border border-orange-200">
                            <div className="flex items-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                                    <i className="fas fa-rocket text-orange-500"></i>
                                </div>
                                <div className="font-semibold text-orange-700">Nom du déploiement</div>
                            </div>
                            <p className="text-sm text-orange-600">
                                Le nom du déploiement configuré dans Azure OpenAI Studio (ex: gpt-4o, gpt-35-turbo).
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </TaleDashbaordMainCard>
    );
};

export default AiConfiguration;